import React, { useRef } from 'react';
import { Box } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { ProductShowcaseSection } from '../components/ProductShowcaseSection';
import {
  HeroSection,
  HomeCta,
  PartnerEvent,
  PipelineScene,
  ValueProps,
  useMarketingScroll,
} from '../components/marketing';
import { marketingLinks } from '../constants/marketingEvent';

/**
 * Marketing home page: hero → value props → pipeline scene → live catalog
 * showcase → ProMaster partnership → CTA.
 *
 * The handoff also places a logo strip, a stat row and a testimonial here.
 * All three are omitted: the logos and the quote named retailers that are not
 * customers, and the stats ("12,000+ SKUs synced daily", "34 active
 * integrations", "99.9% sync uptime", "<1 hr setup") are figures nothing in
 * this system measures. They can come back when there is something real to
 * put in them.
 *
 * `ProductShowcaseSection` is not in the handoff but shows live catalog data,
 * so it sits after the pipeline scene as the "here is the real thing" beat.
 */
export const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const pipelineRef = useRef<HTMLDivElement | null>(null);

  const { pipelineProgress, stageScale } = useMarketingScroll({
    pipeline: pipelineRef,
  });

  // Hero "Book a demo", the PRO meeting CTA, and the bottom HomeCta all open
  // the Google appointment scheduler — same link as /contact.
  const bookDemo = () => window.open(marketingLinks.scheduler, '_blank', 'noopener,noreferrer');

  return (
    <Box>
      <HeroSection onPrimary={bookDemo} onSecondary={() => navigate('/features')} />
      <ValueProps />
      <PipelineScene
        sectionRef={pipelineRef}
        progress={pipelineProgress}
        stageScale={stageScale}
      />
      <ProductShowcaseSection />
      <PartnerEvent onBookMeeting={bookDemo} />
      <HomeCta onBookDemo={bookDemo} />
    </Box>
  );
};

export default HomePage;
